import { combineLatest, map, of, switchMap } from 'rxjs';
import { dispatchAction } from '@ardoq/rxbeach';
import { integrationId$ } from 'integrations/common/streams/integrationId/integrationId$';
import { getTransferConfigsStream } from 'integrations/common/streams/transferConfigs/transferConfigs$';
import type {
  IntegrationWorkspace,
  SavedTransferConfig,
} from '@ardoq/api-types/integrations';
import { emitOverviewActionEvent } from 'integrations/common/tracking/utils';
import { startDuplicateTransferConfigModal } from 'integrations/common/modals/duplicateTransferConfigModal/DuplicateTransferConfigModal';
import {
  bulkDeleteConfigurations,
  loadTransferConfig,
  renameConfiguration,
} from 'integrations/common/streams/transferConfigs/actions';
import { getIntegrationTermsDictionaryStream } from 'integrations/common/streams/integrationTermsDictionary/getIntegrationTermsDictionaryStream';
import type { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { startCopyToTransferConfigModal } from 'integrations/common/modals/copyToTransferConfigModal/CopyToTransferConfigModal';
import { showImportApiPayloadDrawer } from '../../components/importApiPayloadDrawer/ImportApiPayloadDrawer';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';
import { t } from '@ardoq/i18n';
import { toExpandedRowDataSource } from 'integrations/common/components/expandedRowMapping/ExpandedRowMapping';

const onDeleteConfigs =
  (integrationId: IntegrationId) => (configs: SavedTransferConfig[]) => {
    emitOverviewActionEvent(
      configs.length === 1 ? 'config-delete' : 'config-bulk-delete',
      integrationId
    );
    dispatchAction(bulkDeleteConfigurations({ configs }));
  };

const onRenameConfiguration =
  (integrationId: IntegrationId) =>
  (name: string, config: SavedTransferConfig) => {
    dispatchAction(renameConfiguration({ integrationId, name, config }));
  };

const onStartConfigurationRenaming = (integrationId: IntegrationId) => () => {
  emitOverviewActionEvent('config-rename', integrationId);
};

const onWorkspaceClick = (workspace: IntegrationWorkspace) => {
  if (workspace.id) {
    dispatchAction(
      navigate({
        route: '/workspace-manager/:workspaceId/assets',
        params: { workspaceId: workspace.id },
      })
    );
  }
};

export const viewModel$ = integrationId$.pipe(
  switchMap(integrationId =>
    combineLatest([
      of(integrationId),
      getTransferConfigsStream(integrationId),
      getIntegrationTermsDictionaryStream(integrationId),
      i18nLocale$,
      locale$,
      persistentWorkspaces$,
    ])
  ),
  map(
    ([
      integrationId,
      { configs, asyncStatus, selectedConfigIds },
      integrationTermsDictionary,
      i18nLocale,
      locale,
      workspaces,
    ]) => {
      const availableWorkspaceIds = new Set(Object.keys(workspaces.byId));
      const expandedRowDataByConfigId = Object.fromEntries(
        configs.map(config => [
          config._id,
          toExpandedRowDataSource(config.tables, availableWorkspaceIds),
        ])
      );

      const loadConfig = (transferConfig: SavedTransferConfig) => {
        emitOverviewActionEvent('config-edit', integrationId);
        dispatchAction(
          loadTransferConfig({
            transferConfig,
            integrationId,
          })
        );
      };

      const baseActions = [
        {
          label: t(i18nLocale, 'integrations:transferConfigs.edit', 'Edit'),
          onClick: loadConfig,
        },
        {
          label: t(
            i18nLocale,
            'integrations:transferConfigs.duplicate',
            'Duplicate'
          ),
          onClick: (config: SavedTransferConfig) => {
            emitOverviewActionEvent('config-copy', integrationId);
            startDuplicateTransferConfigModal(integrationId, config);
          },
        },
        {
          label: t(
            i18nLocale,
            'integrations:transferConfigs.copyTo',
            'Copy to...'
          ),
          onClick: (config: SavedTransferConfig) => {
            emitOverviewActionEvent('config-copy-to', integrationId);
            startCopyToTransferConfigModal(integrationId, config);
          },
        },
      ];

      const excelActions =
        integrationId === 'excel-v3'
          ? [
              {
                label: t(
                  i18nLocale,
                  'integrations:transferConfigs.importApiPayloadDetails',
                  'Import API payload details...'
                ),
                onClick: (config: SavedTransferConfig) => {
                  emitOverviewActionEvent(
                    'config-generate-api-payload',
                    integrationId
                  );
                  showImportApiPayloadDrawer(config._id);
                },
              },
            ]
          : [];

      return {
        dataSource: configs,
        isLoading: asyncStatus === 'LOADING',
        selectedIds: selectedConfigIds,
        extraSingleItemActions: [...baseActions, ...excelActions],
        onDeleteConfigs: onDeleteConfigs(integrationId),
        onRenameConfiguration: onRenameConfiguration(integrationId),
        onStartConfigurationRenaming:
          onStartConfigurationRenaming(integrationId),
        loadConfig,
        onConfigClick: loadConfig,
        onWorkspaceClick,
        expandedRowDataByConfigId,
        isExpandable: true,
        isClickable: true,
        noConfigsMessage: integrationTermsDictionary.noConfigsMessage,
        i18nLocale,
        locale,
      };
    }
  )
);
